import { ApiResponse } from 'apisauce';

import { GatewayResponse, ProtectedRoute } from '~types/gateway';
import {
    TCreateCollateralAPIParams,
    TCreateNewCustomerAPIParams,
    TCustomerDetails,
    TDeleteCustomerAPIParams,
    TEditCustomerDetailsAPIParams,
    TExportCustomerListingAPIResponse,
    TGetCollateralListingAPIParams,
    TGetCollateralMarketValueAPIParams,
    TGetCustomerDetailsAPIParams,
    TGetCustomerFilterAPIParams,
    TGetCustomerFilterAPIResponse,
    TGetCustomerInventoryAPIParams,
    TGetCustomerInventoryAPIResponse,
    TGetCustomerListingAPIParams,
    TGetCustomerListingAPIResponse,
    TGetCustomerTransactionsAPIParams,
    TGetCustomerTransactionsAPIResponse,
    TGetCollateralListingReduxResponse,
    TCreateReleaseEarmarkAPIParams,
    TExportCustomerListingAPIParams,
    TGetCustomerConversionsAPIParams,
    TGetCustomerConversionsAPIresponse,
    TGetCustomerHoldingListingAPIParams,
    TGetCustomerHoldingListingAPIResponse,
} from '~types/feat/customer';

import { ICustomerGateway } from './customerBase';

import UtilFunctions from '~utils/functions';
import { CustomerAccStatusEnum } from '~utils/enums';
import { TGetReleaseEarmarkListingAPIParams, TGetReleaseEarmarkListingAPIResponse } from '~types/feat/setting';

const { API: { getHeaders } } = UtilFunctions;

export default class CustomerGateway extends ICustomerGateway {
    async getCustomerListing(params: TGetCustomerListingAPIParams): GatewayResponse<TGetCustomerListingAPIResponse> {
        const { authToken, ...restOfGetCustomerListingAPIParams } = params;
        const response: ApiResponse<TGetCustomerListingAPIResponse> = await this.api.get('/customer/getCustomerListing', restOfGetCustomerListingAPIParams, getHeaders(authToken));
        return this.process(response);
    }

    async createNewCustomer(params: TCreateNewCustomerAPIParams): GatewayResponse<null> {
        const { authToken, ...restOfCreateNewCustomerAPIParams } = params;
        const response: ApiResponse<null> = await this.api.post('/customer/createCustomer', restOfCreateNewCustomerAPIParams, getHeaders(authToken));
        return this.process(response);
    }

    async getCustomerDetails(params: TGetCustomerDetailsAPIParams): GatewayResponse<TCustomerDetails> {
        const { authToken, customerId } = params;
        const response: ApiResponse<TCustomerDetails> = await this.api.get(`/customer/getCustomerDetails/${customerId}`, {}, getHeaders(authToken));
        return this.process(response);
    }

    async deleteCustomer(params: TEditCustomerDetailsAPIParams): GatewayResponse<null> {
        const { authToken, customerId, ...restOfParams } = params;
        const response: ApiResponse<null> = await this.api.put(`/customer/editCustomerDetails/${customerId}`, { ...restOfParams, status: CustomerAccStatusEnum.Deactivated }, getHeaders(authToken));
        return this.process(response);
    }

    async editCustomerDetails(params: TEditCustomerDetailsAPIParams): GatewayResponse<null> {
        const { authToken, customerId, ...restOfEditCustomerDetailsAPIParams } = params;
        const response: ApiResponse<null> = await this.api.put(`/customer/editCustomerDetails/${customerId}`, { ...restOfEditCustomerDetailsAPIParams }, getHeaders(authToken));
        return this.process(response);
    }

    async getCustomerTransactions(params: TGetCustomerTransactionsAPIParams): GatewayResponse<TGetCustomerTransactionsAPIResponse> {
        const { authToken, customerId, index } = params;
        const response: ApiResponse<TGetCustomerTransactionsAPIResponse> = await this.api.get('/transaction/getTransactionList', { index, customerId }, getHeaders(authToken));
        return this.process(response);
    }

    async getAllCustomers(params: TGetCustomerFilterAPIParams): GatewayResponse<TGetCustomerFilterAPIResponse> {
        const { authToken, searchQuery } = params;
        const response: ApiResponse<TGetCustomerFilterAPIResponse> = await this.api.get('/customer/getCustomerFilter', { searchQuery }, getHeaders(authToken));
        return this.process(response);
    }

    async getCustomerInventory(params: TGetCustomerInventoryAPIParams): GatewayResponse<TGetCustomerInventoryAPIResponse> {
        const { authToken, ...restOfGetCustomerInventoryParams } = params;
        const response: ApiResponse<TGetCustomerInventoryAPIResponse> = await this.api.get('/customer/getCustomerInventory', restOfGetCustomerInventoryParams, getHeaders(authToken));
        return this.process(response);
    }

    async exportCustomerListing(params: TExportCustomerListingAPIParams): GatewayResponse<TExportCustomerListingAPIResponse> {
        const { authToken, ...restOfGetCustomerInventoryParams } = params;
        const response: ApiResponse<TExportCustomerListingAPIResponse> = await this.api.get('/customer/exportCustomerListing', restOfGetCustomerInventoryParams, getHeaders(authToken));
        return this.process(response);
    }

    async createCollateral(params: TCreateCollateralAPIParams): GatewayResponse<string | null> {
        const { authToken, ...restOfCreateNewCustomerAPIParams } = params;
        const response: ApiResponse<null> = await this.api.post('/collateral/createCollateral', restOfCreateNewCustomerAPIParams, getHeaders(authToken));
        return this.process(response);
    }

    async getCollateralMarketValue(params: TGetCollateralMarketValueAPIParams): GatewayResponse<number | undefined> {
        const { authToken, ...restOfGetCollateralMarketValueParams } = params;
        const response: ApiResponse<number | undefined> = await this.api.get('/collateral/getCollateralMarketValue', restOfGetCollateralMarketValueParams, getHeaders(authToken));
        return this.process(response);
    }

    async getCollateralListing(params: TGetCollateralListingAPIParams): GatewayResponse<TGetCollateralListingReduxResponse> {
        const { authToken, ...restOfGetCollateralListingParams } = params;
        const response: ApiResponse<TGetCollateralListingReduxResponse> = await this.api.get('/collateral/getCollateralListing', restOfGetCollateralListingParams, getHeaders(authToken));
        return this.process(response);
    }

    async createReleaseEarmark(params: TCreateReleaseEarmarkAPIParams): GatewayResponse<null> {
        const { authToken, ...body } = params;
        const response: ApiResponse<null> = await this.api.post('/transaction/createEarmarkRelease', body, getHeaders(authToken));
        return this.process(response);
    }

    async getReleaseEarmarkListing(params: TGetReleaseEarmarkListingAPIParams): GatewayResponse<TGetReleaseEarmarkListingAPIResponse> {
        const { authToken, ...restOfGetReleaseEarmarkListingAPIParams } = params;
        const response: ApiResponse<TGetReleaseEarmarkListingAPIResponse> = await this.api.get('/transaction/getEarmarkReleaseListing', restOfGetReleaseEarmarkListingAPIParams, getHeaders(authToken));
        return this.process(response);
    }

    async getCustomerConversionListing(params: TGetCustomerConversionsAPIParams): GatewayResponse<TGetCustomerConversionsAPIresponse> {
        const { authToken, id, ...restOfGetCustomerConversionListingAPIParams } = params;
        const response: ApiResponse<TGetCustomerConversionsAPIresponse> = await this.api.get(`/customer/${id}/conversions`, restOfGetCustomerConversionListingAPIParams, getHeaders(authToken));
        return this.process(response);
    }

    async getCustomerHoldingsListing(params: TGetCustomerHoldingListingAPIParams): GatewayResponse<TGetCustomerHoldingListingAPIResponse> {
        const { authToken, ...restOfGetCustomerHoldingsListingAPIParams } = params;
        const response: ApiResponse<TGetCustomerHoldingListingAPIResponse> = await this.api.get(`/customer/getCustomerHoldings`, restOfGetCustomerHoldingsListingAPIParams, getHeaders(authToken));
        return this.process(response);
    }
}
